/*
 * Copyright (c) 2025, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 LLC. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import MaterialTable, { Column } from "@material-table/core";
import { AddRounded, ArrowDropDown, ChevronLeft, ChevronRight, Refresh } from "@material-ui/icons";
import React, { forwardRef, Ref, RefAttributes, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { Api, ApiKeyV2, Application, ApplicationReducer, ApplicationState, UserReducer } from "../../../../data/interfaces";
import { connect } from "react-redux";
import { Box, Button, Container, Typography } from "@material-ui/core";
import { useStyles } from "./style";
import { PrimaryButtonRounded } from "../../../../elements/PrimaryButtonRounded";
import APIKeyRegenerationConfirmation from "./APIKeyRegenerationConfirmation";
import ApiKeyDeletionConfirmation from "./ApiKeyDeletionConfirmation";
import { ApplicationPermission } from "../../../../util/app-permissions";
import { AppPermission } from "../../../../data/emus";
import { useHistory } from "react-router-dom";
import { PATH_SEPARATOR } from "../../../../data/constants";
import Settings from "../../../../conf/settings";

export enum ViewType {
    APIView = 'APIView',
    ApplicationView = 'ApplicationView'
}

interface ApiKeysListProps {
    apiInfo: Api,
    viewType: ViewType,
    isApiKeysLoading: boolean,
    apiKeys: ApiKeyV2[],
    applicationReducer: ApplicationReducer,
    onClose: () => void,
    onGenerateApiKey? : () => void
    userReducer: UserReducer,
}

interface RowData {
    id: string,
    keyName: string,
    applicationId: string,
    applicationName: string,
    apiId: string,
    apiName: string,
    scopes: string[]
}

function ApiKeysList(props: ApiKeysListProps) {

    const { apiInfo,apiKeys, isApiKeysLoading, viewType, applicationReducer, userReducer, onClose, onGenerateApiKey } = props;
    const [apiKeyRegenerationWizardOpen, setApiKeyRegenerationWizardOpen] = React.useState(false);

    const [apiKeyDeletionWizardOpen, setApiKeyDeletionWizardOpen] = React.useState(false);
    const classes = useStyles();
    const history = useHistory();
    const apps = applicationReducer.applications?.list;
    const { userInfo, user } = userReducer;

    const selectedOrgHandleName = userReducer.selectedOrg.handle;
    const scopes = user?.allowedScopes;
    const generateApiKey = () => {
        if (onGenerateApiKey) {
            onGenerateApiKey();
        }
    }

    const [selectedApiKey, setSelectedApiKey] = useState("");
    const [pageNumber, setPageNumber] = useState(0);

    const intl = useIntl();

    const handleRegenerateApiKey = (apiKey: RowData) => {
        setSelectedApiKey(apiKey.id);
        setApiKeyRegenerationWizardOpen(true);
    }

    const handleRevokeApiKey = (apiKey: RowData) => {
        setSelectedApiKey(apiKey.id);
        setApiKeyDeletionWizardOpen(true);
    }


    const [list, setListData] = useState<RowData[]>([]);
    const [rowsPerPage, setRowsPerPage] = useState(5);

    const getNextPageIcon = (props: RefAttributes<SVGSVGElement>,
        ref: Ref<SVGSVGElement>) => <ChevronRight {...props} ref={ref} />;

    const getPreviousPageIcon = (props: RefAttributes<SVGSVGElement>,
        ref: Ref<SVGSVGElement>) => <ChevronLeft {...props} ref={ref} />;

    const getArrowIcon = (props: RefAttributes<SVGSVGElement>,
        ref: Ref<SVGSVGElement>) => <ArrowDropDown{...props} ref={ref} />;

    const appTableI18n = {
        application: intl.formatMessage({
            id: 'application.home.appList.table.column.application',
            defaultMessage: 'APPLICATION'
        }),
        status: intl.formatMessage({
            id: 'application.home.appList.table.column.status',
            defaultMessage: 'STATUS'
        }),
        subscriptions: intl.formatMessage({
            id: 'application.home.appList.table.column.subscriptions',
            defaultMessage: 'SUBSCRIPTIONS'
        }),
        created: intl.formatMessage({
            id: 'application.home.appList.table.column.created',
            defaultMessage: 'CREATED'
        }),
        nextPageLabel: intl.formatMessage({
            id: 'application.home.appList.table.nextPageLabel',
            defaultMessage: 'Next'
        }),
        previousPageLabel: intl.formatMessage({
            id: 'application.home.appList.table.previousPageLabel',
            defaultMessage: 'Previous'
        })
    };

    const tableIcons = {
        NextPage: forwardRef(getNextPageIcon),
        PreviousPage: forwardRef(getPreviousPageIcon),
        SortArrow: forwardRef(getArrowIcon)
    };
    
    const handlePageNumberChange = (page: number) => {
        setPageNumber(page * rowsPerPage / list.length)
    };


    const mapApiKeysToRows = () => {
        setListData([]);
        apiKeys?.forEach((apiKey: ApiKeyV2) => {
            setListData((prevState: RowData[]) => (
                [...prevState, {
                    id: apiKey.keys[0].id as string,
                    keyName: apiKey.alias,
                    applicationId: apiKey.application.id,
                    applicationName: apiKey.application.name,
                    apiId: apiKey.api.id,
                    apiName: apiKey.api.name,
                    scopes: apiKey.scopes
                }]
            ));
        });
    };

    useEffect(() => {
        mapApiKeysToRows();
    }, [apiKeys]);
    

    if (apiKeys.length === 0) {
        return null;
    }

    const apiKeyNameColumn = intl.formatMessage({
        id: 'apiKeyList.table.column.apiKeyName',
        defaultMessage: 'Name'
    });

    const applicationColumn = intl.formatMessage({
        id: 'apiKeyList.table.column.application',
        defaultMessage: 'APPLICATION'
    });

    const actionsColumn = intl.formatMessage({
        id: 'apiKeyList.table.column.actions',
        defaultMessage: 'ACTIONS'
    });

    const scopesColumn = intl.formatMessage({
        id: 'apiKeyList.table.column.scopes',
        defaultMessage: 'SCOPES'
    });

    const Actions = (btnProps: {apiKey: RowData}) => {
            const { apiKey } = btnProps;
            const matchingApp = apps?.find(app => app.applicationId === apiKey.applicationId);
            return (
                <div>
                    <Box flex={1} display="flex" flexDirection="row" justifyContent="flex-end">
                        { ApplicationPermission.hasPermission(AppPermission.WRITE, matchingApp, userInfo, scopes) && (
                            <>
                                   <Button
                                    variant="text"
                                    className={classes.hoverBtn}
                                    onClick={() => {
                                        handleRegenerateApiKey(apiKey);
                                    }}
                                    startIcon={<Refresh />}
                                >
                                    {intl.formatMessage({
                                        id: 'apiKeyList.table.column.actions.regenerate',
                                        defaultMessage: 'Regenerate'
                                    })}
                                </Button>
                                <Button
                                    variant="text"
                                    className={classes.deleteBtn}
                                    disableRipple={true}
                                    disableTouchRipple={true}
                                    onClick={() => {
                                        handleRevokeApiKey(apiKey);
                                    }}
                                >
                                    {intl.formatMessage({
                                        id: 'apiKeyList.table.column.actions.delete',
                                        defaultMessage: 'Delete'
                                    })}
                                </Button>
                            </>
                        ) }
                        
                    </Box>
                </div>
            );
        };

    const ActionsColumn = (rowData: RowData) => {
        return (
            <Actions apiKey={rowData} />
        );
    };

    const ApplicationColumn = (rowData: RowData) => {
        return (
            <Typography
                onClick={() => history.push(PATH_SEPARATOR + selectedOrgHandleName + '/applications/' + rowData.applicationId)}
                className={classes.hyperlinkNameColumn}
            >
                {rowData.applicationName}
            </Typography>
        );
    };

    const ApiColumn = (rowData: RowData) => {
        return (
            <Typography
                onClick={() => history.push(PATH_SEPARATOR + selectedOrgHandleName + '/apis/' + rowData.apiId)}
                className={classes.hyperlinkNameColumn}
            >
                {rowData.apiName}
            </Typography>
        );
    }

    const ApiKeyNameColumn = (rowData: RowData) => {
        return (
            <Typography className={classes.apiKeyName}>
                {rowData.keyName}
            </Typography>
        );
    };

    const ScopeColumn = (rowData: RowData) => {
        if (rowData.scopes.length === 0) {
            return (
                <Typography className={classes.apiKeyName}>
                    {intl.formatMessage({
                        id: 'apiKeyList.table.column.scopes.none',
                        defaultMessage: '-'
                    })}
                </Typography>
            );
        }
        return (
            <Typography className={classes.apiKeyName}>
                {rowData.scopes.join(",")}
            </Typography>
        );
    }
    

    const onRegenerationDialogClose = () => {
        setApiKeyRegenerationWizardOpen(false);
    }

    const onDeletionDialogClose = () => {
        setApiKeyDeletionWizardOpen(false);
        onClose();
    }
    

    const columns = () => {
        if (viewType === ViewType.APIView) {
            return [
                { title: apiKeyNameColumn, field: 'name', width: '30%', render: ApiKeyNameColumn },
                { title: applicationColumn, field: 'application', width: '25%', render: ApplicationColumn },
                { title: actionsColumn, width: '20%', render: ActionsColumn }
            ] as Column<RowData>[];
        } else {
            return [
                { title: apiKeyNameColumn, field: 'name', width: '30%', render: ApiKeyNameColumn },
                { title: "Api", field: 'api', width: '25%', render: ApiColumn },
                { title: actionsColumn, width: '20%', render: ActionsColumn }
            ] as Column<RowData>[];
        }
    }

    if (isApiKeysLoading) {
        return null;
    }

    return (
        <React.Fragment>
            <Container 
                maxWidth="lg"
                className={classes.container}
                classes={{ root: classes.componentPadding }}
                >
                { viewType === ViewType.APIView && (
                <Box pb={2} mt={3} flexDirection="row" justifyContent="flex-end" display="flex">
                    <PrimaryButtonRounded
                        dataTestId="create-subscription-btn"
                        onClick={generateApiKey}
                        startIcon={<AddRounded />}
                        text={"Generate API Key"}
                    />
                </Box>
                )}
                <div className={["config-list", classes.containerList].join(" ")}>
                    <MaterialTable
                        key={list.toString() + rowsPerPage.toString()}
                        onRowsPerPageChange={(pageSize) => { setRowsPerPage(pageSize) }}
                        onPageChange={handlePageNumberChange}
                        page={pageNumber}
                        options={{
                            pageSize: 5,
                            showFirstLastPageButtons: false,
                            pageSizeOptions: Array.from(Array(15).keys()),
                            showTitle: false,
                            search: false,
                            sorting: false,
                            headerStyle: { height: 30, pointerEvents: 'none' },
                        }}
                        columns={columns()}
                        icons={tableIcons}
                        data={list}
                        localization={{
                            pagination: {
                                nextTooltip: appTableI18n.nextPageLabel,
                                previousTooltip: appTableI18n.previousPageLabel
                            }
                        }}
                    />
                </div>
            </Container>
            <APIKeyRegenerationConfirmation 
                open={apiKeyRegenerationWizardOpen} 
                onClose={onRegenerationDialogClose} 
                selectedApiKey={selectedApiKey}
            />
            <ApiKeyDeletionConfirmation
                open={apiKeyDeletionWizardOpen}
                onClose={onDeletionDialogClose}
                selectedApiKey={selectedApiKey}
            />
        </React.Fragment>
    );
}

const mapStateToProps = (state: ApplicationState) => {
    return {
        isApiKeysLoading: state.apiKeysReducer.isLoading,
        apiKeys: state.apiKeysReducer.apiKeys,
        applicationReducer: state.applicationReducer,
        userReducer: state.userReducer
    }
}

const mapDispatchToProps = {
};

export default connect(mapStateToProps, mapDispatchToProps)(ApiKeysList);
